import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import alumniApi from "../../config/alumniApi";
import styles from "./AlumniAuth.module.css";

const initialForm = {
  full_name: "",
  mobile_number: "",
  email: "",
  student_id: "",
  otp: "",
};

const AlumniRegister = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSendOtp = async () => {
    setError("");
    setInfo("");
    if (!/^[6-9]\d{9}$/.test(form.mobile_number)) {
      setError("Enter a valid 10-digit mobile number");
      return;
    }
    setLoading(true);
    try {
      await alumniApi.post("/alumni/auth/send-otp", {
        mobile_number: form.mobile_number,
        purpose: "REGISTRATION",
      });
      setOtpSent(true);
      setInfo("OTP sent to your mobile number.");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!otpSent) {
      setError("Please verify your mobile number with OTP first.");
      return;
    }
    if (!form.full_name || !form.mobile_number || !form.otp) {
      setError("Please fill in all required fields.");
      return;
    }

    const payload = {
      ...form,
    };

    setLoading(true);
    try {
      await alumniApi.post("/alumni/auth/register", payload);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to submit registration request.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className={styles.page}>
        <div className={`${styles.card} ${styles.wideCard}`}>
          <h1 className={styles.title}>Request Submitted</h1>
          <p className={styles.subtitle}>
            Thank you! Your registration request has been received. An alumni administrator
            will review your request and approve your account. You'll be able to log in
            using your registered mobile number once your request is approved.
          </p>
          <Link to="/alumni/login" className={styles.linkBtn}>Back to login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={`${styles.card} ${styles.wideCard}`}>
        <h1 className={styles.title}>Alumni Registration Request</h1>
        <p className={styles.subtitle}>
          Complete the registration request below. Your request will be reviewed by the alumni administrator before your account is activated.
        </p>

        {error && <div className={styles.error}>{error}</div>}
        {info && <div className={styles.success}>{info}</div>}

        <form onSubmit={handleSubmit}>
          <div className={styles.field}>
            <label className={styles.label}>Full Name *</label>
            <input className={styles.input} value={form.full_name} onChange={update("full_name")} required />
          </div>

          <div className={styles.row}>
            <div className={styles.field}>
              <label className={styles.label}>Mobile Number *</label>
              <input
                className={styles.input}
                type="tel"
                maxLength={10}
                value={form.mobile_number}
                onChange={(e) =>
                  setForm((f) => ({ ...f, mobile_number: e.target.value.replace(/\D/g, "") }))
                }
                disabled={otpSent}
                required
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Email</label>
              <input className={styles.input} type="email" value={form.email} onChange={update("email")} />
            </div>
          </div>

          {!otpSent ? (
            <button
              type="button"
              className={styles.button}
              onClick={handleSendOtp}
              disabled={loading}
            >
              {loading ? "Sending OTP..." : "Verify Mobile Number"}
            </button>
          ) : (
            <div className={styles.field}>
              <label className={styles.label}>Enter OTP *</label>
              <input
                className={styles.input}
                maxLength={6}
                value={form.otp}
                onChange={(e) => setForm((f) => ({ ...f, otp: e.target.value.replace(/\D/g, "") }))}
                required
              />
            </div>
          )}

          <div className={styles.field}>
            <label className={styles.label}>PP Enrollment / Student ID (if known)</label>
            <input className={styles.input} value={form.student_id} onChange={update("student_id")} />
          </div>


          <button type="submit" className={styles.button} disabled={loading || !otpSent}>
            {loading ? "Submitting..." : "Submit Registration Request"}
          </button>
        </form>

        <div className={styles.footerRow}>
          <span>Already approved?</span>
          <Link to="/alumni/login" className={styles.linkBtn}>Login here</Link>
        </div>
      </div>
    </div>
  );
};

export default AlumniRegister;
